import * as React from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn, getInitials } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { PaginationControls } from '@/components/pagination-controls';
import { DEFAULT_PAGE_SIZE, type PaginationMetadata } from '@/types/pagination';
import { fetchClientsPaged } from '@/lib/data-service';
import type { Client } from '@/types';

const SEARCH_DEBOUNCE_MS = 500;

interface ClientComboboxProps {
  clients: Client[];
  value?: string;
  onValueChange: (value: string, client: Client | undefined) => void;
  placeholder?: string;
  recentClientIds?: string[];
  maxRecentClients?: number;
  disabled?: boolean;
}

export function ClientCombobox({
  clients,
  value,
  onValueChange,
  placeholder = 'Select a client...',
  recentClientIds = [],
  maxRecentClients = 5,
  disabled = false,
}: ClientComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [popoverSearch, setPopoverSearch] = React.useState('');

  // "Show More" table state. The table holds one server-returned page, never the whole list.
  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState<number>(DEFAULT_PAGE_SIZE);
  const [pagedClients, setPagedClients] = React.useState<Client[]>([]);
  const [pagination, setPagination] = React.useState<PaginationMetadata | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const requestIdRef = React.useRef(0);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // A new search or page size always starts at the first page.
  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, pageSize]);

  React.useEffect(() => {
    if (!modalOpen) return;

    // Only the newest request may write state; a slower earlier page must not overwrite it.
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    fetchClientsPaged({ page, pageSize, search: debouncedSearch })
      .then((response) => {
        if (requestIdRef.current !== requestId) return;
        setPagedClients(response.data);
        setPagination(response.pagination);
      })
      .catch((err) => {
        if (requestIdRef.current !== requestId) return;
        console.error('Failed to load clients:', err);
        setError('Failed to load clients. Please try again.');
      })
      .finally(() => {
        if (requestIdRef.current === requestId) setLoading(false);
      });
  }, [modalOpen, page, pageSize, debouncedSearch]);

  // Get recent clients
  const recentClients = React.useMemo(() => {
    return recentClientIds
      .map((id) => clients.find((c) => String(c.id) === id))
      .filter((c): c is Client => c !== undefined)
      .slice(0, maxRecentClients);
  }, [clients, recentClientIds, maxRecentClients]);

  // Filter all clients for popover search
  const popoverFilteredClients = React.useMemo(() => {
    if (!popoverSearch.trim()) return [];
    const query = popoverSearch.toLowerCase();
    return clients.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        client.code?.toLowerCase().includes(query) ||
        client.contactName?.toLowerCase().includes(query) ||
        client.contactEmail?.toLowerCase().includes(query)
    );
  }, [clients, popoverSearch]);

  // Get selected client
  const selectedClient = clients.find((client) => String(client.id) === value);

  const handleSelect = (client: Client) => {
    onValueChange(String(client.id), client);
    setOpen(false);
    setModalOpen(false);
  };

  const handleShowMore = () => {
    setOpen(false);
    setModalOpen(true);
    setSearchQuery('');
    setDebouncedSearch('');
    setPage(1);
  };

  return (
    <>
      {/* Main Combobox - Shows Recent Clients */}
      <Popover open={disabled ? false : open} onOpenChange={disabled ? undefined : (isOpen) => { setOpen(isOpen); if (!isOpen) setPopoverSearch(''); }}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between"
          >
            {selectedClient ? selectedClient.name : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search clients..."
              value={popoverSearch}
              onValueChange={setPopoverSearch}
            />
            <CommandList>
              {popoverSearch.trim() ? (
                <>
                  <CommandEmpty>No clients found.</CommandEmpty>
                  <CommandGroup heading="Search Results">
                    {popoverFilteredClients.map((client) => (
                      <CommandItem
                        key={client.id}
                        value={String(client.id)}
                        onSelect={() => handleSelect(client)}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            value === String(client.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <Avatar className="mr-2 h-8 w-8 rounded-md">
                          <AvatarImage src={client.logo} alt={client.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                            {getInitials(client.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col">
                          <span className="font-medium">{client.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {client.code && <>{client.code}</>}
                            {client.contactName && ` • ${client.contactName}`}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              ) : (
                <>
                  <CommandEmpty>No recent clients found.</CommandEmpty>
                  {/* Recent Clients */}
                  {recentClients.length > 0 && (
                    <CommandGroup heading="Recent Clients">
                      {recentClients.map((client) => (
                        <CommandItem
                          key={client.id}
                          value={String(client.id)}
                          onSelect={() => handleSelect(client)}
                          className="cursor-pointer"
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              value === String(client.id) ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <Avatar className="mr-2 h-8 w-8 rounded-md">
                            <AvatarImage src={client.logo} alt={client.name} />
                            <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                              {getInitials(client.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col">
                            <span className="font-medium">{client.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {client.code && <>{client.code}</>}
                              {client.contactName && ` • ${client.contactName}`}
                            </span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {/* Show More Button */}
                  <CommandGroup>
                    <CommandItem
                      onSelect={handleShowMore}
                      className="cursor-pointer justify-center font-medium text-primary"
                    >
                      <ChevronsUpDown className="mr-2 h-4 w-4" />
                      Show More ({clients.length - recentClients.length} more clients)
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Modal Dialog - Shows All Clients in Table */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Client</DialogTitle>
            <DialogDescription>
              Choose a client from the list below. Click on a row to select.
            </DialogDescription>
          </DialogHeader>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, code, contact..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Table Container with Scroll */}
          <div className="flex-1 overflow-auto border rounded-md">
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[40px]"></TableHead>
                  <TableHead className="w-[56px]"></TableHead>
                  <TableHead>Client Name</TableHead>
                  <TableHead>Client Code</TableHead>
                  <TableHead>Contact Name</TableHead>
                  <TableHead>Contact Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-destructive py-8">
                      {error}
                    </TableCell>
                  </TableRow>
                ) : loading && pagedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Loading clients...
                    </TableCell>
                  </TableRow>
                ) : pagedClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No clients found matching your search.
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedClients.map((client) => (
                    <TableRow
                      key={client.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSelect(client)}
                      data-state={value === String(client.id) ? 'selected' : undefined}
                    >
                      <TableCell>
                        <Check
                          className={cn(
                            'h-4 w-4',
                            value === String(client.id) ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Client.logo is the only image the client API returns; it is often unset,
                            in which case the initials fallback shows. */}
                        <Avatar className="h-8 w-8 rounded-md">
                          <AvatarImage src={client.logo} alt={client.name} />
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold text-xs rounded-md">
                            {getInitials(client.name)}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>
                      <TableCell className="font-medium">{client.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.code || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contactName || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.contactEmail || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination: "Showing X-Y of Z" left, navigation centre, rows per page right */}
          {pagination && (
            <PaginationControls
              pagination={pagination}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              layout="summary"
              itemLabel="clients"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
